import { Point, PointClickEventObject } from 'highcharts';
import { keyBy } from 'lodash';
import { Dictionary } from 'ramda';
import { useCallback, useMemo, useState } from 'react';
import { useAsyncExport } from 'api/async-export/use-async-export';
import {
  AggregateType,
  Axis,
  ChartOptions,
  excludedTaskFieldsForCustomChart,
  Field as WidgetField,
  FieldClass,
  FieldOption,
  GroupByDefaultProjectFields,
  GroupByDefaultTaskFields,
  HorizontalAxisDefaultProjectFields,
  HorizontalAxisDefaultTaskFields,
  NativeDateFields,
  SimpleFilter,
  VerticalAxisDefaultProjectFields,
  VerticalAxisDefaultTaskFields,
  WidgetDefaultNumberFields,
  WidgetNativeNumberFields,
  WidgetNativeValues,
  WidgetType,
  NumberWidgetTypes,
  DrillDownExportType,
  DrillDownPayload,
} from 'api/dashboard/widget';
import { FieldSection } from 'api/field-sections/types';
import { Action, ObjectType } from 'api/pushes';
import { Field } from 'api/tasks';
import { toastService } from 'components/molecules/toast';
import { DashboardDateFieldFilterOperationTypes } from 'components/organisms/dashboard/use-filter-fields';
import { FieldTypes } from 'components/organisms/module-settings/fields/types';
import {
  canShowInTaskFieldsSettings,
  getType,
} from 'components/organisms/module-settings/fields/utils';
import { DateField } from 'components/organisms/tasks/fields';
import {
  DateFieldFilter,
  isOptional,
} from 'components/organisms/tasks/filters/date';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { useUserWsUpdates } from 'hooks/use-user-ws-updates';
import { useAllFieldsQuery, useProjectFieldsQuery } from 'queries';
import { useDashboardQuery } from 'queries/dashboard';
import { useProjectFieldSectionsQuery } from 'queries/project-field-sections';
import { useWidgetExportsMutation } from 'queries/widgets';

interface CustomXaxis {
  categories: string[];
  categoryDrillDowns?: SimpleFilter[];
}

interface ChartPoint extends Point {
  drillDowns?: SimpleFilter;
}
interface PointClickEvent extends PointClickEventObject {
  point: ChartPoint;
}

export const getFilterForDrillWidget = (
  chartOptions: ChartOptions | null,
  event: PointClickEvent,
): SimpleFilter | undefined => {
  const {
    point: {
      category,
      series: { name },
    },
  } = event;

  const { drillDown } = event.point as any;

  if (drillDown) {
    return drillDown;
  }

  if (chartOptions?.series) {
    const { series, xAxis } = chartOptions;
    const result = series?.find((s) => name === s.name) as
      | {
          drillDown: SimpleFilter;
        }
      | undefined;

    const { categories, categoryDrillDowns } = xAxis as unknown as CustomXaxis;

    const categoryIndex = (categories ?? []).findIndex((c) => c === category);

    const categoryDrillDown = (categoryDrillDowns ?? [])[categoryIndex];

    if (!result && !categoryDrillDown) {
      return undefined;
    }

    return {
      nativeFields: [
        ...(result?.drillDown?.nativeFields ?? []),
        ...(categoryDrillDown?.nativeFields ?? []),
      ],
      customFields: [
        ...(result?.drillDown?.customFields ?? []),
        ...(categoryDrillDown?.customFields ?? []),
      ],
    };
  }

  return undefined;
};

export interface GroupedFields {
  label: string;
  options: FieldOption[];
}

type IsFieldsLoading = boolean;
interface GetProjectFields {
  groupedProjectNumberFieldOptions: GroupedFields[];
  groupedProjectCategoricalFieldOptions: GroupedFields[];
  projectGroupByFieldOptions: GroupedFields[];
  isfieldsLoading: IsFieldsLoading;
}

interface GetTaskFields {
  groupedTaskNumberFieldOptions: GroupedFields[];
  groupedTaskCategoricalFieldOptions: GroupedFields[];
  taskGroupByFieldOptions: GroupedFields[];
  allFields: Field[];
  isTaskFieldsLoading: IsFieldsLoading;
}

const groupFieldOptions = (
  fieldId: number,
  fieldMap: Dictionary<Field>,
): FieldOption => {
  const label = fieldMap[fieldId].fieldLabel || fieldMap[fieldId].fieldName;

  switch (fieldMap[fieldId].fieldName) {
    case WidgetNativeNumberFields.ARR:
      return {
        label,
        value: {
          name: WidgetNativeValues.ARR,
          fieldClass: FieldClass.NativeField,
        },
      };
    case WidgetNativeNumberFields.ProjectFee:
      return {
        label,
        value: {
          name: WidgetNativeValues.ProjectFee,
          fieldClass: FieldClass.NativeField,
        },
      };
    default:
      return {
        label,
        value: {
          fieldId: fieldMap[fieldId].fieldId,
          fieldClass: FieldClass.CustomField,
        },
      };
  }
};

const generateFieldOptions = (
  fieldSections: FieldSection[],
  fieldList: Field[],
) => {
  const fieldMap = keyBy(fieldList, 'fieldId');

  return fieldSections
    .filter((section) => section.fieldOrder)
    .map((section) => {
      const groupedFieldOptions = section.fieldOrder
        .filter((fieldId) => fieldMap[fieldId])
        .map((fieldId) => groupFieldOptions(fieldId, fieldMap));

      return {
        label: section.fieldSectionName,
        options: groupedFieldOptions,
      };
    })
    .filter((mappedSection) => mappedSection.options.length > 0);
};

export const useProjectFieldOptions = (
  type: WidgetType.CustomCard | WidgetType.CustomChart,
): GetProjectFields => {
  const { data: fieldList, isLoading: isFieldsLoading } =
    useProjectFieldsQuery();

  const { data: fieldSections, isLoading: isFieldSectionsLoading } =
    useProjectFieldSectionsQuery();

  const loadingStatus = isFieldSectionsLoading || isFieldsLoading;

  const translate = useTranslate();

  const defaultFieldNames = useMemo(
    () =>
      [
        ...Object.values(VerticalAxisDefaultProjectFields),
        ...Object.values(HorizontalAxisDefaultProjectFields),
        ...Object.values(GroupByDefaultProjectFields),
      ].map((fieldName) => fieldName.toLowerCase()),
    [],
  );
  const filteredFieldList = useMemo(() => {
    return fieldList
      ? fieldList.filter((field) => {
          return !defaultFieldNames.includes(field.fieldName.toLowerCase());
        })
      : [];
  }, [defaultFieldNames, fieldList]);

  const filteredNumberFields = useMemo(() => {
    return fieldList
      ? filteredFieldList.filter((field) => {
          return field.fieldDataType === FieldTypes.Number && field.isEnabled;
        })
      : [];
  }, [fieldList, filteredFieldList]);

  const filteredCategoricalFieldList = useMemo(() => {
    return fieldList
      ? filteredFieldList.filter((field) => {
          return (
            [
              FieldTypes.SingleUserSelect,
              FieldTypes.SingleSelect,
              FieldTypes.Date,
              FieldTypes.SingleSelect,
              FieldTypes.MultiSelect,
              FieldTypes.Boolean,
            ].includes(getType(field)) && field.isEnabled
          );
        })
      : [];
  }, [fieldList, filteredFieldList]);

  const projectGroupByFields = useMemo(() => {
    return filteredCategoricalFieldList.filter((field) => {
      return ![FieldTypes.Date].includes(getType(field)) && field.isEnabled;
    });
  }, [filteredCategoricalFieldList]);

  const numberFieldOptions = useMemo((): GroupedFields[] => {
    const projectFields =
      filteredNumberFields && fieldSections
        ? generateFieldOptions(fieldSections, filteredNumberFields)
        : [];

    const defaultNumberFields = Object.values(WidgetDefaultNumberFields).map(
      (field) => {
        return {
          label: translate(
            `dashboard.custom.widget.default_field.label.${field}` as TranslationKey,
          ),
          value: {
            name: field,
            fieldClass: FieldClass.DefaultField,
          },
        };
      },
    );

    const defaultChartNumberFields = Object.values(
      VerticalAxisDefaultProjectFields,
    ).map((field) => {
      return {
        label: translate(
          `dashboard.custom.widget.default_field.label.${field}` as TranslationKey,
        ),
        value: {
          name: field,
          fieldClass: FieldClass.DefaultField,
        },
      };
    });

    const options =
      WidgetType.CustomCard === type
        ? defaultNumberFields
        : defaultChartNumberFields;

    return [
      {
        label: translate('dashboard.custom_card.default_fields.label'),
        options,
      },
      ...projectFields,
    ];
  }, [fieldSections, filteredNumberFields, translate, type]);

  const categoricalFieldOptions = useMemo(() => {
    const projectFields =
      filteredCategoricalFieldList && fieldSections
        ? generateFieldOptions(fieldSections, filteredCategoricalFieldList)
        : [];

    const defaultCategoricalFields = Object.values(
      HorizontalAxisDefaultProjectFields,
    ).map((field) => {
      return {
        label: translate(
          `dashboard.custom.widget.default_field.label.${field}` as TranslationKey,
        ),
        value: {
          name: field,
          fieldClass: FieldClass.DefaultField,
        },
      };
    });

    return [
      {
        label: translate('dashboard.custom_card.default_fields.label'),
        options: defaultCategoricalFields,
      },
      ...projectFields,
    ];
  }, [fieldSections, filteredCategoricalFieldList, translate]);

  const groupByFieldOptions = useMemo(() => {
    const groupByFields =
      projectGroupByFields && fieldSections
        ? generateFieldOptions(fieldSections, projectGroupByFields)
        : [];

    const defaultGroupByFields = Object.values(GroupByDefaultProjectFields).map(
      (field) => {
        return {
          label: translate(
            `dashboard.custom.widget.default_field.label.${field}` as TranslationKey,
          ),
          value: {
            name: field,
            fieldClass: FieldClass.NativeField,
          },
        };
      },
    );

    return [
      {
        label: translate('dashboard.custom_card.default_fields.label'),
        options: defaultGroupByFields,
      },
      ...groupByFields,
    ];
  }, [fieldSections, projectGroupByFields, translate]);

  return {
    groupedProjectNumberFieldOptions: numberFieldOptions,
    groupedProjectCategoricalFieldOptions: categoricalFieldOptions,
    projectGroupByFieldOptions: groupByFieldOptions,
    isfieldsLoading: loadingStatus,
  };
};

export const useTaskFieldsOptions = (): GetTaskFields => {
  const { data, isLoading } = useAllFieldsQuery();
  const translate = useTranslate();

  const defaultFieldNames = useMemo(
    () =>
      [
        ...Object.values(VerticalAxisDefaultTaskFields),
        ...Object.values(HorizontalAxisDefaultTaskFields),
        ...Object.values(GroupByDefaultTaskFields),
      ].map((fieldName) => fieldName.toLowerCase()),
    [],
  );

  const taskFields = useMemo(() => {
    const filteredOutTaskFields = data
      ? data
          .filter((field) => {
            return (
              field.objectType === 'TASK' &&
              field.isEnabled &&
              !defaultFieldNames.includes(field.fieldName.toLowerCase()) &&
              !excludedTaskFieldsForCustomChart.includes(
                field.fieldName.toLowerCase(),
              )
            );
          })
          .filter(canShowInTaskFieldsSettings)
      : [];

    return filteredOutTaskFields;
  }, [data, defaultFieldNames]);

  const taskFieldOptions = useMemo(() => {
    const taskNumberFields = taskFields
      .filter((field) => {
        return field.fieldDataType === FieldTypes.Number;
      })
      .map((field) => {
        return {
          label: field.fieldLabel || field.fieldName,
          value: {
            fieldId: field.fieldId,
            fieldClass: FieldClass.CustomField,
          },
        };
      });

    const taskOtherFields = taskFields.filter((field) => {
      return [
        FieldTypes.SingleUserSelect,
        FieldTypes.MultiUserSelect,
        FieldTypes.Date,
        FieldTypes.SingleSelect,
        FieldTypes.MultiSelect,
        FieldTypes.Boolean,
      ].includes(getType(field));
    });

    const taskCategoricalFieldOptions = taskOtherFields.map((field) => {
      return {
        label: field.fieldLabel || field.fieldName,
        value: {
          fieldId: field.fieldId,
          fieldClass: FieldClass.CustomField,
        },
      };
    });

    const groupByFields = taskOtherFields
      .filter((field) => {
        return ![FieldTypes.Date].includes(getType(field));
      })
      .map((field) => {
        return {
          label: field.fieldLabel || field.fieldName,
          value: {
            fieldId: field.fieldId,
            fieldClass: FieldClass.CustomField,
          },
        };
      });

    const verticalAxisDefaultTaskFieldOptions = Object.values(
      VerticalAxisDefaultTaskFields,
    ).map((field) => {
      return {
        label: translate(
          `dashboard.custom.widget.task.default_field.label.${field}` as TranslationKey,
        ),
        value: {
          name: field,
          fieldClass: FieldClass.DefaultField,
        },
      };
    });

    const horizontalAxisDefaultTaskFieldOptions = Object.values(
      HorizontalAxisDefaultTaskFields,
    ).map((field) => {
      return {
        label: translate(
          `dashboard.custom.widget.task.default_field.label.${field}` as TranslationKey,
        ),
        value: {
          name: field,
          fieldClass: FieldClass.DefaultField,
        },
      };
    });

    const taskGroupByDefaultFields = Object.values(
      GroupByDefaultTaskFields,
    ).map((field) => {
      return {
        label: translate(
          `dashboard.custom.widget.task.default_field.label.${field}` as TranslationKey,
        ),
        value: {
          name: field,
          fieldClass: FieldClass.NativeField,
        },
      };
    });

    return {
      groupedTaskNumberFieldOptions: [
        {
          label: 'Default Fields',
          options: verticalAxisDefaultTaskFieldOptions,
        },
        {
          label: 'Task Number Fields',
          options: taskNumberFields,
        },
      ],
      groupedTaskCategoricalFieldOptions: [
        {
          label: 'Default Fields',
          options: horizontalAxisDefaultTaskFieldOptions,
        },
        {
          label: 'Task Fields',
          options: taskCategoricalFieldOptions,
        },
      ],
      taskGroupByFieldOptions: [
        {
          label: 'Default Fields',
          options: taskGroupByDefaultFields,
        },
        {
          label: 'Task groupby fields',
          options: groupByFields,
        },
      ],
    };
  }, [taskFields, translate]);

  return {
    ...taskFieldOptions,
    allFields: data || [],
    isTaskFieldsLoading: isLoading,
  };
};

export const fieldLabelSelector = (
  field: WidgetField,
  customFields,
): TranslationKey => {
  switch (field.fieldClass) {
    case FieldClass.NativeField:
    case FieldClass.DefaultField:
      if ('name' in field) {
        return `dashboard.custom.widget.default_field.label.${field.name}` as TranslationKey;
      }

      return 'dashboard.custom_card.field.label.empty';
    case FieldClass.CustomField:
      if ('fieldId' in field) {
        const customField = customFields.find((_customField) => {
          return _customField.fieldId === field.fieldId;
        });

        return customField.fieldLabel;
      }

      return 'dashboard.custom_card.field.label.empty';

    default:
      return 'dashboard.custom_card.field.label.empty';
  }
};

export const aggregateRules = (
  value: string,
): { aggType?: AggregateType; disableTotal: boolean; disableAvg: boolean } => {
  switch (value) {
    case WidgetDefaultNumberFields.CSAT:
      return {
        aggType: AggregateType.Average,
        disableTotal: true,
        disableAvg: false,
      };
    case WidgetDefaultNumberFields.CustomerCount:
    case WidgetDefaultNumberFields.ProjectCount:
    case VerticalAxisDefaultProjectFields.ProjectCount:
    case VerticalAxisDefaultTaskFields.TaskCount:
      return {
        aggType: AggregateType.Total,
        disableTotal: false,
        disableAvg: true,
      };
    default:
      return {
        disableTotal: false,
        disableAvg: false,
      };
  }
};

export const transformField = (input: FieldOption): Axis => {
  const fieldClass =
    input.value.fieldClass === FieldClass.DefaultField ||
    input.value.fieldClass === FieldClass.NativeField
      ? FieldClass.NativeField
      : FieldClass.DefaultField;

  let name;

  if ('name' in input.value) {
    name =
      input.value.name === WidgetNativeValues.ProjectFee
        ? WidgetNativeNumberFields.ProjectFee
        : input.value.name;
  }

  if (input.value.fieldClass === FieldClass.CustomField) {
    return {
      title: input.label,
      field: input.value,
    };
  }

  return {
    title: input.label,
    field: {
      name,
      fieldClass,
    },
  };
};

export const setAggType = (
  field: FieldOption,
  aggType: AggregateType,
): AggregateType => {
  if (
    'name' in field.value &&
    ['count'].includes(field.value.name) &&
    aggType === AggregateType.Total
  ) {
    return AggregateType.Count;
  }

  return aggType;
};

export const dateFilterOperationOptions = (field: DateField) =>
  DashboardDateFieldFilterOperationTypes.filter(
    (x) => !field.metaData?.required || !isOptional(x),
  ).map(
    (type) =>
      ({
        field,
        type,
      } as any as DateFieldFilter),
  );

export const isNativeDateField = (field: WidgetField | undefined): boolean => {
  if (field && 'name' in field) {
    return NativeDateFields.includes(field.name as any);
  }

  return false;
};

export const byNumberTypeWidget = (widgetType: WidgetType): boolean =>
  NumberWidgetTypes.includes(widgetType);

export const useGetChartAndCardWidgetCount = (dashboardId: number) => {
  const { data } = useDashboardQuery(dashboardId);
  const widgets = useMemo(
    () =>
      (data?.widgets ?? []).filter(
        (widget) => !widget.deleted && widget.enabled,
      ),
    [data],
  );

  const numberTypeWidgetsCount = useMemo(
    () =>
      (widgets ?? []).filter((widget) => byNumberTypeWidget(widget.widgetType))
        .length,
    [widgets],
  );
  const chartTypeWidgetsCount = useMemo(
    () =>
      (widgets ?? []).filter((widget) => !byNumberTypeWidget(widget.widgetType))
        .length,
    [widgets],
  );

  return { numberTypeWidgetsCount, chartTypeWidgetsCount };
};

export const useDashboardExports = (widgetId: number) => {
  const [isExportInProgress, setExportProgress] = useState(false);
  const translate = useTranslate();

  const { handleExport } = useAsyncExport({
    isExportInProgress,
    onExportCompleted: () => setExportProgress(false),
  });

  const { mutate: widgetExportsMutation } = useWidgetExportsMutation(
    widgetId || -1,
  );

  const handleWidgetExportSuccess = useCallback(
    (data) => {
      switch (data.drillDownExportType) {
        case DrillDownExportType.PROJECT:
          setExportProgress(false);
          window.open(data.exportMetaData.downloadUrl, '_blank');

          toastService({
            title: translate('dashboards.exports.sucess.title'),
            message: translate('dashboards.exports.sucess.message'),
          }).success();

          break;
        case DrillDownExportType.TASK:
          break;
        default:
          break;
      }
    },
    [translate],
  );

  useUserWsUpdates(handleExport, [Action.Create, ObjectType.AsyncProcessor]);

  const exportFn = useCallback(
    (payload: DrillDownPayload) => {
      setExportProgress(true);
      widgetExportsMutation(payload, {
        onSuccess: handleWidgetExportSuccess,
      });
    },
    [widgetExportsMutation, handleWidgetExportSuccess],
  );

  return {
    isExportInProgress,
    exportFn,
  };
};

const getDecimalValue = (value: number): string => {
  return `${Math.floor(value * 100) / 100}`;
};

export const formatNumber = (value: number): string => {
  if (value > 1e9) {
    return `${getDecimalValue(value / 1e9)}B`;
  }

  if (value > 1e6) {
    return `${getDecimalValue(value / 1e6)}M`;
  }

  if (value > 1e3) {
    return `${getDecimalValue(value / 1e3)}K`;
  }

  return `${parseFloat(value.toFixed(2))}`;
};
